import React, { useEffect, useMemo, useState } from 'react';
import './ThicknessPage.css';
import './TestsPage.css';
import Lens3DPreview from './Lens3DPreview';
import { FRAME_SHAPES } from './frameShapes';
import {
  computeThicknessStudy,
  createDefaultThicknessForm,
  DEFAULT_DESIGNS,
  DEFAULT_INDICES,
} from './thicknessEngine';
import { deleteTest, getTests, saveTest, updateTest } from './testManager';

export default function ManualThicknessTestPage({ goTo }) {
  const [form, setForm] = useState(() => createDefaultThicknessForm({}));
  const [studyMeta, setStudyMeta] = useState({
    clientName: '',
    frameReference: '',
    notes: '',
  });
  const [saveState, setSaveState] = useState('idle');
  const [savedTestId, setSavedTestId] = useState(null);
  const [viewMode, setViewMode] = useState('2d');
  const [savedStudies, setSavedStudies] = useState([]);
  const [currentId, setCurrentId] = useState(null);

  const study = useMemo(() => computeThicknessStudy(form), [form]);

  const [selectedMaterial, setSelectedMaterial] = useState('');

const availableMaterials = useMemo(() => {
  const right = (study?.right?.comparisons ?? []).map(c => c.label);
  const left = (study?.left?.comparisons ?? []).map(c => c.label);

  return Array.from(new Set([...right, ...left]));
}, [study]);

useEffect(() => {
  if (!availableMaterials.length) {
    setSelectedMaterial('');
    return;
  }

  setSelectedMaterial(prev =>
    availableMaterials.includes(prev)
      ? prev
      : availableMaterials[0]
  );
}, [availableMaterials]);

  useEffect(() => {
    loadSavedStudies();
  }, []);

  async function loadSavedStudies() {
  try {
    const items = await getTests();
    const manualItems = items.filter((item) => item.type === 'manualThickness');
    setSavedStudies(manualItems);
  } catch (error) {
    console.error(error);
  }
}

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleRxChange = (eye, key, value) => {
    setForm((prev) => ({
      ...prev,
      rx: {
        ...prev.rx,
        [eye]: {
          ...prev.rx[eye],
          [key]: value,
        },
      },
    }));
  };

  const toggleIndex = (indexValue) => {
    setForm((prev) => {
      const selected = new Set(prev.selectedIndices ?? DEFAULT_INDICES);
      if (selected.has(indexValue)) selected.delete(indexValue);
      else selected.add(indexValue);
      return {
        ...prev,
        selectedIndices: Array.from(selected).sort((a, b) => a - b),
        compareAllIndices: false,
      };
    });
  };

  const toggleDesign = (designValue) => {
    setForm((prev) => {
      const selected = new Set(prev.selectedDesigns ?? DEFAULT_DESIGNS);
      if (selected.has(designValue)) selected.delete(designValue);
      else selected.add(designValue);
      return {
        ...prev,
        selectedDesigns: Array.from(selected),
        compareAllIndices: false,
      };
    });
  };

  const handleSave = async () => {
  try {
    setSaveState('saving');

    const payload = {
      type: 'manualThickness',
      title:
        studyMeta.clientName?.trim() ||
        studyMeta.frameReference?.trim() ||
        'Calcul épaisseur manuel',
      meta: studyMeta,
      input: form,
      result: study,
      updatedAt: Date.now(),
    };

    let saved;

    if (savedTestId) {
      saved = await updateTest(savedTestId, payload);
    } else {
      saved = await saveTest(payload);
      if (saved?.id) {
        setSavedTestId(saved.id);
        setCurrentId(saved.id);
      }
    }

    await loadSavedStudies();

    setSaveState('saved');
    window.setTimeout(() => setSaveState('idle'), 1800);
  } catch (error) {
    console.error(error);
    setSaveState('error');
    window.setTimeout(() => setSaveState('idle'), 2200);
  }
};

  const handleLoad = (item) => {
  setCurrentId(item.id);
  setSavedTestId(item.id);
  setStudyMeta(item.meta || { clientName: item.title || '', frameReference: '', notes: '' });
  setForm(item.input || createDefaultThicknessForm({}));
};

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette étude ?')) return;
    await deleteTest(id);
    if (currentId === id) {
      handleNew();
    }
    loadSavedStudies();
  };

  const handleNew = () => {
  setCurrentId(null);
  setSavedTestId(null);
  setStudyMeta({ clientName: '', frameReference: '', notes: '' });
  setForm(createDefaultThicknessForm({}));
};

  return (
    <div className="test-shell">
      <div className="test-panel glass-card">
        <div className="test-topbar">
          <div>
            <h1>Calcul d&apos;épaisseur manuel</h1>
            <p>Version indépendante des archives, avec saisie manuelle et sauvegarde locale.</p>
          </div>
          <div className="test-actions">
            <button onClick={() => goTo('tests')}>⬅ Tests</button>
            <button onClick={handleNew}>＋ Nouvelle étude</button>
            <button onClick={handleSave} disabled={saveState === 'saving'}>
              {saveState === 'saving' ? 'Enregistrement...' : '💾 Enregistrer'}
            </button>
          </div>
        </div>

        <div className="manual-thickness-layout">
          <div>
            <section className="thickness-panel">
              <h2>Référence étude</h2>
              <div className="study-meta-grid">
  <label>
    Nom client / dossier
    <input
      className="meta-field-input"
      type="text"
      value={studyMeta.clientName}
      onChange={(e) =>
        setStudyMeta((prev) => ({ ...prev, clientName: e.target.value }))
      }
    />
  </label>

  <label>
    Référence monture
    <input
      className="meta-field-input"
      type="text"
      value={studyMeta.frameReference}
      onChange={(e) =>
        setStudyMeta((prev) => ({ ...prev, frameReference: e.target.value }))
      }
    />
  </label>
</div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                Notes
                <input
  className="meta-field-input"
  type="text"
  value={studyMeta.notes}
  onChange={(e) => setStudyMeta((prev) => ({ ...prev, notes: e.target.value }))}
/>
              </label>
            </section>

            <section className="thickness-panel" style={{ marginTop: 20 }}>
              <h2>Données monture</h2>
              <div className="form-grid two-columns">
                <label>
                  Forme
                  <select value={form.frameShape} onChange={(e) => handleChange('frameShape', e.target.value)}>
                    {FRAME_SHAPES.map((shape) => (
                      <option key={shape.id} value={shape.id}>{shape.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Type de verre
                  <select value={form.lensType} onChange={(e) => handleChange('lensType', e.target.value)}>
                    <option value="singleVision">Unifocal</option>
                    <option value="progressive">Progressif</option>
                  </select>
                </label>
                <label>
                  Largeur A (mm)
                  <input type="number" value={form.A} onChange={(e) => handleChange('A', Number(e.target.value))} />
                </label>
                <label>
                  Hauteur B (mm)
                  <input type="number" value={form.B} onChange={(e) => handleChange('B', Number(e.target.value))} />
                </label>
                <label>
                  Pont DBL (mm)
                  <input type="number" value={form.DBL} onChange={(e) => handleChange('DBL', Number(e.target.value))} />
                </label>
                <label>
                  Épaisseur mini (mm)
                  <input type="number" step="0.1" value={form.centerThickness} onChange={(e) => handleChange('centerThickness', Number(e.target.value))} />
                </label>
                <label>
                  Hauteur OD (mm)
                  <input type="number" step="0.1" value={form.fittingHeightRight} onChange={(e) => handleChange('fittingHeightRight', Number(e.target.value))} />
                </label>
                <label>
                  Hauteur OG (mm)
                  <input type="number" step="0.1" value={form.fittingHeightLeft} onChange={(e) => handleChange('fittingHeightLeft', Number(e.target.value))} />
                </label>
                <label>
                  DP OD (mm)
                  <input type="number" step="0.1" value={form.pdRight} onChange={(e) => handleChange('pdRight', Number(e.target.value))} />
                </label>
                <label>
                  DP OG (mm)
                  <input type="number" step="0.1" value={form.pdLeft} onChange={(e) => handleChange('pdLeft', Number(e.target.value))} />
                </label>
                {form.lensType === 'progressive' && (
                  <label>
                    Addition
                    <input type="number" step="0.25" value={form.addition} onChange={(e) => handleChange('addition', Number(e.target.value))} />
                  </label>
                )}
              </div>
            </section>

            <section className="thickness-panel" style={{ marginTop: 20 }}>
              <h2>Rx</h2>
              <div className="rx-grid">
                {['od', 'og'].map((eye) => (
                  <div key={eye} className="rx-card">
                    <h3>{eye.toUpperCase()}</h3>
                    <label>
  Sphère
  <input
    type="text"
    value={form.rx[eye].sphere}
    onChange={(e) => handleRxChange(eye, 'sphere', e.target.value)}
  />
</label>
                    <label>
                      Cylindre
                      <input type="text" value={form.rx[eye].cylinder} onChange={(e) => handleRxChange(eye, 'cylinder', e.target.value)} />
                    </label>
                    <label>
                      Axe
                      <input type="number" min="0" max="180" value={form.rx[eye].axis} onChange={(e) => handleRxChange(eye, 'axis', Number(e.target.value))} />
                    </label>
                  </div>
                ))}
              </div>

              <div className="index-box">
                <label className="checkbox-line">
                  <input
                    type="checkbox"
                    checked={form.compareAllIndices}
                    onChange={(e) => handleChange('compareAllIndices', e.target.checked)}
                  />
                  Comparer automatiquement tous les indices
                </label>

                {!form.compareAllIndices && (
                  <>
                    <div className="index-chip-row">
                      {DEFAULT_INDICES.map((indexValue) => (
                        <button
                          key={indexValue}
                          type="button"
                          className={`index-chip ${(form.selectedIndices ?? []).includes(indexValue) ? 'active' : ''}`}
                          onClick={() => toggleIndex(indexValue)}
                        >
                          {indexValue.toFixed(2)}
                        </button>
                      ))}
                    </div>
                    <div className="index-chip-row" style={{ marginTop: 10 }}>
                      {DEFAULT_DESIGNS.map((designValue) => (
                        <button
                          key={designValue}
                          type="button"
                          className={`index-chip ${(form.selectedDesigns ?? []).includes(designValue) ? 'active' : ''}`}
                          onClick={() => toggleDesign(designValue)}
                        >
                          {designValue}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="thickness-panel full-width-table" style={{ marginTop: 20 }}>
              <h2>Comparatif d&apos;épaisseur bord / centre</h2>
              <ResultsTable study={study} />
            </section>
          </div>

          <aside className="saved-studies-panel">
            <h2>Prévisualisation</h2>
            <div className="test-actions" style={{ marginBottom: 14 }}>
              <button type="button" className={viewMode === '2d' ? 'active' : ''} onClick={() => setViewMode('2d')}>2D</button>
              <button type="button" className={viewMode === '3d' ? 'active' : ''} onClick={() => setViewMode('3d')}>3D</button>
            </div>

            <div className="result-summaries">
              <SummaryCard label="OD" eyeResult={study.right} />
              <SummaryCard label="OG" eyeResult={study.left} />
            </div>
            {viewMode === '3d' && availableMaterials.length > 0 && (
  <div className="tests-material-select">
    <label>
      Matériau affiché
      <select
        value={selectedMaterial}
        onChange={(e) => setSelectedMaterial(e.target.value)}
      >
        {availableMaterials.map((label) => (
          <option key={label} value={label}>
            {label}
          </option>
        ))}
      </select>
    </label>
  </div>
)}

            <div className="svg-grid" style={{ marginTop: 14 }}>
              {viewMode === '2d' ? (
                <>
                  <LensPreview title="OD" eyeResult={study.right} A={study.meta.A} B={study.meta.B} />
                  <LensPreview title="OG" eyeResult={study.left} A={study.meta.A} B={study.meta.B} />
                </>
              ) : (
  <>
    <Lens3DPreview
      title="OD"
      eyeResult={study.right}
      selectedMaterialLabel={selectedMaterial}
    />
    <Lens3DPreview
      title="OG"
      eyeResult={study.left}
      selectedMaterialLabel={selectedMaterial}
    />
  </>
)}
            </div>

            <h3 style={{ marginTop: 22 }}>Études sauvegardées</h3>
            <div className="study-list">
              {savedStudies.length === 0 && <p className="helper-note">Aucune étude enregistrée pour le moment.</p>}
              {savedStudies.map((item) => (
                <div key={item.id} className="study-list-item">
                  <p>
                    <strong>{item.title}</strong>
                    <span>{new Date(item.updatedAt || item.createdAt).toLocaleDateString()}</span>
                  </p>
                  <div className="study-list-actions">
                    <button type="button" onClick={() => handleLoad(item)}>Ouvrir</button>
                    <button type="button" onClick={() => handleDelete(item.id)}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}


function SummaryCard({ label, eyeResult }) {
  const comparisons = eyeResult?.comparisons ?? [];

  if (!comparisons.length) {
    return (
      <div className="summary-card">
        <h3>{label}</h3>
        <p>Aucune donnée disponible</p>
      </div>
    );
  }

  const best = comparisons.reduce((acc, item) => {
    const accValue = acc?.thicknessType === 'center' ? acc?.centerThickness : acc?.edgeThickness;
    const itemValue = item?.thicknessType === 'center' ? item?.centerThickness : item?.edgeThickness;
    return itemValue < accValue ? item : acc;
  }, comparisons[0]);

  const displayedThickness = best?.thicknessType === 'center' ? best?.centerThickness : best?.edgeThickness;

  return (
    <div className="summary-card">
      <h3>{label}</h3>
      <p>Puissance retenue : {eyeResult.effectivePower.toFixed(2)} D</p>
      <p>Diamètre utile : {eyeResult.usefulDiameter.toFixed(1)} mm</p>
      <p>Zone max probable : {eyeResult.maxPoint.zone}</p>
      <p>Type d&apos;épaisseur : {best?.thicknessType === 'center' ? 'centre' : 'bord'}</p>
      <p>Bord : {best?.edgeThickness?.toFixed(1)} mm</p>
      <p>Centre : {best?.centerThickness?.toFixed(1)} mm</p>
      <p>Indice retenu : {best?.label ?? best?.index?.toFixed(2)} → {displayedThickness?.toFixed(1)} mm</p>
    </div>
  );
}

function ResultsTable({ study }) {
  const rows = study.right.comparisons.map((od, index) => ({ od, og: study.left.comparisons[index] }));

  return (
    <div className="table-wrap">
      <table className="thickness-table">
        <thead>
          <tr>
            <th>Indice</th>
            <th>OD bord</th>
            <th>OD centre</th>
            <th>Gain OD</th>
            <th>OG bord</th>
            <th>OG centre</th>
            <th>Gain OG</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ od, og }) => (
            <tr key={od.label}>
              <td>{od.label}</td>
              <td>{od?.thicknessType === 'center' ? `max ${od?.maxEdgeThickness?.toFixed(1)} mm` : `${od?.edgeThickness?.toFixed(1)} mm`}</td>
              <td>{od?.thicknessType === 'center' ? `${od?.centerThickness?.toFixed(1)} mm` : ''}</td>
              <td>{formatGain(od?.cosmeticGainVs15)}</td>
              <td>{og?.thicknessType === 'center' ? `max ${og?.maxEdgeThickness?.toFixed(1)} mm` : `${og?.edgeThickness?.toFixed(1)} mm`}</td>
              <td>{og?.thicknessType === 'center' ? `${og?.centerThickness?.toFixed(1)} mm` : ''}</td>
              <td>{formatGain(og?.cosmeticGainVs15)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatGain(value) {
  if (!Number.isFinite(value)) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)} mm`;
}

function getThicknessColor(thickness, min, max) {
  if (max <= min) return 'rgb(120, 180, 255)';
  const t = (thickness - min) / (max - min);
  const r = Math.round(255 * t);
  const g = Math.round(160 * (1 - t));
  const b = Math.round(255 * (1 - t));
  return `rgb(${r}, ${g}, ${b})`;
}

function buildHeatSegments(thicknessMap, mirrorX, width, height, scale) {
  if (!Array.isArray(thicknessMap) || thicknessMap.length < 2) return [];
  const thicknessValues = thicknessMap.map((p) => p.thickness);
  const minThickness = Math.min(...thicknessValues);
  const maxThickness = Math.max(...thicknessValues);

  return thicknessMap.map((point, index) => {
    const next = thicknessMap[(index + 1) % thicknessMap.length];
    return {
      x1: width / 2 + mirrorX(point.x) * scale,
      y1: height / 2 - point.y * scale,
      x2: width / 2 + mirrorX(next.x) * scale,
      y2: height / 2 - next.y * scale,
      color: getThicknessColor(point.thickness, minThickness, maxThickness),
    };
  });
}

function LensPreview({ title, eyeResult, A, B }) {
  const mirrorX = (x) => -x;
  const width = 260;
  const height = 220;
  const margin = 18;
  const scale = Math.min((width - margin * 2) / A, (height - margin * 2) / B);
  const isPositive = eyeResult.effectivePower > 0;

  const points = eyeResult.shapePoints
    .map((point) => `${width / 2 + mirrorX(point.x) * scale},${height / 2 - point.y * scale}`)
    .join(' ');

  const heatSegments = buildHeatSegments(eyeResult.thicknessMap, mirrorX, width, height, scale);
  const fitX = width / 2 + mirrorX(eyeResult.fittingPoint.x) * scale;
  const fitY = height / 2 - eyeResult.fittingPoint.y * scale;
  const maxX = width / 2 + mirrorX(eyeResult.maxPoint.x) * scale;
  const maxY = height / 2 - eyeResult.maxPoint.y * scale;

  return (
    <div className="lens-preview-card">
      <h3>{title}</h3>
      <svg viewBox={`0 0 ${width} ${height}`} className="lens-preview-svg" role="img" aria-label={`Prévisualisation ${title}`}>
        <polygon points={points} className="frame-outline" />
        {heatSegments.map((segment, index) => (
          <line key={`${title}-${index}`} x1={segment.x1} y1={segment.y1} x2={segment.x2} y2={segment.y2} stroke={segment.color} strokeWidth="4" strokeLinecap="round" />
        ))}
        <line x1={width / 2} y1={height / 2} x2={fitX} y2={fitY} className="helper-line" />
        <circle cx={width / 2} cy={height / 2} r="4" className="center-point" />
        <circle cx={fitX} cy={fitY} r="4" className="fit-point" />
        <circle cx={maxX} cy={maxY} r="4" className="max-point" />
      </svg>
      <p>{isPositive ? 'Verre plus' : 'Verre moins'} · DU {eyeResult.usefulDiameter.toFixed(1)} mm</p>
    </div>
  );
}
